// src/cart/CartContext.js
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from "react";

const CartContext = createContext(null);

// Minimal product shape we expect to add to cart
// { id, name, price, currency, image, quantity, metadata? }

const initialState = {
  items: [], // array of {id, name, price, currency, image, quantity}
  currency: "USD", // default; we can make it dynamic later
  subtotal: 0,
  itemCount: 0,
};

function calcTotals(items) {
  const subtotal = items.reduce((sum, it) => sum + it.price * it.quantity, 0);
  const itemCount = items.reduce((sum, it) => sum + it.quantity, 0);
  return { subtotal, itemCount };
}

function reducer(state, action) {
  switch (action.type) {
    case "LOAD":
      return { ...state, ...action.payload };

    case "ADD": {
      const { item } = action;
      const existing = state.items.find((x) => x.id === item.id);
      let items;
      if (existing) {
        items = state.items.map((x) =>
          x.id === item.id
            ? { ...x, quantity: x.quantity + (item.quantity || 1) }
            : x
        );
      } else {
        items = [...state.items, { ...item, quantity: item.quantity || 1 }];
      }
      const { subtotal, itemCount } = calcTotals(items);
      return { ...state, items, subtotal, itemCount };
    }

    case "REMOVE": {
      const items = state.items.filter((x) => x.id !== action.id);
      const { subtotal, itemCount } = calcTotals(items);
      return { ...state, items, subtotal, itemCount };
    }

    case "SET_QTY": {
      const { id, quantity } = action;
      const items = state.items
        .map((x) => (x.id === id ? { ...x, quantity } : x))
        .filter((x) => x.quantity > 0);
      const { subtotal, itemCount } = calcTotals(items);
      return { ...state, items, subtotal, itemCount };
    }

    case "CLEAR": {
      return { ...initialState, currency: state.currency };
    }

    case "SET_CURRENCY": {
      return { ...state, currency: action.currency };
    }

    default:
      return state;
  }
}

export function CartProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  // Load from localStorage once
  useEffect(() => {
    try {
      const raw = localStorage.getItem("cart_v1");
      if (raw) {
        const saved = JSON.parse(raw);
        dispatch({ type: "LOAD", payload: saved });
      }
    } catch {}
  }, []);

  // Persist on every change
  useEffect(() => {
    try {
      localStorage.setItem("cart_v1", JSON.stringify(state));
    } catch {}
  }, [state]);

  const api = useMemo(
    () => ({
      items: state.items,
      subtotal: state.subtotal,
      itemCount: state.itemCount,
      currency: state.currency,

      addItem: (item) => dispatch({ type: "ADD", item }),
      removeItem: (id) => dispatch({ type: "REMOVE", id }),
      setQty: (id, quantity) => dispatch({ type: "SET_QTY", id, quantity }),
      clearCart: () => dispatch({ type: "CLEAR" }),
      setCurrency: (currency) => dispatch({ type: "SET_CURRENCY", currency }),
    }),
    [state]
  );

  return <CartContext.Provider value={api}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
